import React, { useEffect, useState } from "react";
import { Button, Form, Alert, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker"; // Import react-datepicker
import "react-datepicker/dist/react-datepicker.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import Cookies from "js-cookie";
import { userProfileUpdate } from "../../Common/data/userProfileUpdate";
import { getUserProfile } from "../../Common/data/getUserProfile";
import { useNavigate } from "react-router-dom";
import { UserProfileData } from "../../Common/data/userProfileUpdate";

interface BioDataFormProps {
    onNext?: () => void; // Make onNext prop optional
}

// Define a type for the status
type Status = "pending" | "verified" | "rejected" | "null";

const BioDataForm: React.FC<BioDataFormProps> = ({ onNext }) => {
    const [status, setStatus] = useState<Status>("null");
    const [loading, setLoading] = useState(true);
    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    // Load saved form values from cookies or use default values
    const savedFormValues = Cookies.get("bioDataForm")
        ? JSON.parse(Cookies.get("bioDataForm")!)
        : {};

    const initialValues = {
        fullName: "",
        email: "",
        phoneNumber: "",
        linkedinProfile: "",
        dob: null as Date | null,
        profileImage: undefined as File | undefined, // Update to use undefined
        gender: "", // Add gender field
    };

    const validationSchema = Yup.object().shape({
        phoneNumber: Yup.string().required("Phone number is required"),
        dob: Yup.date().required("Date of birth is required"),
        linkedinProfile: Yup.string()
            .required("LinkedIn profile is required")
            .url("Invalid URL format")
            .test("is-https", "URL should include https://", (value) =>
                value ? value.startsWith("https://") : false
            ),
        gender: Yup.string().required("Gender is required"), // Validate gender
        profileImage: Yup.mixed().required("Profile image is required"), // Validate profileImage
    });

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const userData = await getUserProfile();
                const bioDataStatus = userData.results.bioDataStatus;
                setStatus(bioDataStatus || "null"); // Show form if status is null or empty
                setProfileImageUrl(userData.results.profile_image_url || null);
                console.log("Data", userData);

                formik.setValues({
                    fullName: userData.results.fullName || "",
                    email: userData.results.email || "",
                    phoneNumber: userData.results.phoneNumber || "",
                    linkedinProfile: userData.results.linkedinProfile || "",
                    dob: userData.results.dateOfBirth ? new Date(userData.results.dateOfBirth) : null,
                    profileImage: undefined, // Include profileImage as undefined when setting the values
                    gender: userData.results.gender || "", // Initialize gender from user data
                });

                // Save fetched user profile in cookies
                Cookies.set("bioDataForm", JSON.stringify(userData.results), { expires: 1 });

            } catch (error: any) {
                console.error("Error fetching user profile:", error.message);
                setErrorMessage("Error fetching user profile");
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, []);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            if (formik.errors.dob) {
                setErrorMessage("Please select your date of birth.");
                return;
            }
            if (
                !values.fullName ||
                !values.phoneNumber ||
                !values.linkedinProfile ||
                !values.gender ||
                !values.profileImage ||
                !values.dob
            ) {
                setErrorMessage("Please fill in all required fields.");
                return;
            }
    
            console.log("Form data being sent:", values);
    
            try {
                const userData: UserProfileData = {
                    fullName: values.fullName,
                    phoneNumber: values.phoneNumber,
                    linkedinProfile: values.linkedinProfile,
                    dateOfBirth: values.dob ? values.dob.toISOString().split("T")[0] : "",
                    profileImage: values.profileImage,
                    gender: values.gender,
                };

                if (values.email === initialValues.email) {
                    userData.email = initialValues.email;
                }

                console.log("Values being sent to API:", userData);
    
                const apiResponse = await userProfileUpdate(userData);
    
                if (apiResponse.status === 200) {
                    setErrorMessage(apiResponse.message || "An error occurred.");
                    return;
                }
    
                console.log("API Response:", apiResponse);

                // Clear saved form data on successful submission
                Cookies.remove("bioDataForm");
    
                setStatus("pending");
                if (onNext) onNext();
                navigate("/application");
            } catch (error: any) {
                console.error("Error updating user profile:", error.message);
                setErrorMessage("Error updating user profile");
            }
        },
    });

    // Save form data in cookies whenever an input changes
    useEffect(() => {
        Cookies.set("bioDataForm", JSON.stringify(formik.values), { expires: 1 });
    }, [formik.values]);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            formik.setFieldValue("profileImage", file);

            const reader = new FileReader();
            reader.onload = (e) => {
                setProfileImageUrl(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            console.log("Selected file:", file);
        }
    };

    const handleDateChange = (date: Date | null) => {
        formik.setFieldValue('dob', date); // Set the date value
    };

    if (loading) {
        return <Spinner animation="border" variant="primary" />;
    }

    return (
        <>
            <h5 className="mb-4">Bio-data</h5>
            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
            {status === "pending" && (
                <Alert variant="info">Your form has been submitted pending approval.</Alert>
            )}
            {status === "verified" && (
                <Alert variant="success">Your bio data has been verified. You can now go to the next phase.</Alert>
            )}
            {status === "rejected" && (
                <Alert variant="danger">Your bio data has been rejected.</Alert>
            )}
            {status === "null" && (
                <>
                    <Alert variant="warning">
                        Please upload a clear image showing your face.
                    </Alert>
                    <Form onSubmit={formik.handleSubmit} encType="multipart/form-data">
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <Form.Group controlId="fullName">
                                <Form.Label>Full Name <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formik.values.fullName}
                                        readOnly
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group controlId="email">
                                    <Form.Label>Email <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={formik.values.email}
                                        readOnly
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <Form.Group controlId="phoneNumber">
                                    <Form.Label>Phone Number <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formik.values.phoneNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
                                    />
                                    <Form.Control.Feedback type="invalid">{formik.errors.phoneNumber}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group controlId="linkedinProfile">
                                    <Form.Label>LinkedIn Profile <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formik.values.linkedinProfile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="URL should include https://"
                                        isInvalid={formik.touched.linkedinProfile && !!formik.errors.linkedinProfile}
                                    />
                                    <Form.Control.Feedback type="invalid">{formik.errors.linkedinProfile}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                            <Form.Group controlId="dob">
                                    <Form.Label>Date of Birth <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <br />
                                    <DatePicker
                                        selected={formik.values.dob} // Pass the date object directly
                                        onChange={handleDateChange}
                                        dateFormat="yyyy-MM-dd"
                                        className={`form-control ${formik.touched.dob && formik.errors.dob ? 'is-invalid' : ''}`}
                                        placeholderText="Select date"
                                    />
                                    {formik.touched.dob && formik.errors.dob && (
                                        <div className="invalid-feedback">{formik.errors.dob}</div>
                                    )}
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group controlId="gender">
                                    <Form.Label>Gender <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={formik.values.gender}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        isInvalid={formik.touched.gender && !!formik.errors.gender}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>

                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{formik.errors.gender}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>
                        <Form.Group controlId="profileImage">
                            <Form.Label>Profile Image <span style={{ color: 'red' }}>*</span></Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                isInvalid={formik.touched.profileImage && !!formik.errors.profileImage}
                            />
                            <Form.Control.Feedback type="invalid">{formik.errors.profileImage}</Form.Control.Feedback>
                        </Form.Group>
                        {profileImageUrl && (
                                <div className="mt-3">
                                    <img src={profileImageUrl} alt="Profile" style={{ maxWidth: "150px", borderRadius: "50%" }} />
                                </div>
                            )}
                        {errorMessage && <div className="text-danger">{errorMessage}</div>}
                        <div className="d-flex justify-content-between mt-5">
                            <Button variant="primary" type="submit" disabled={!formik.isValid || loading}>
                                {loading ? <Spinner as="span" animation="border" size="sm" /> : "Submit"}
                            </Button>
                        </div>
                    </Form>
                </>
            )}
        </>
    );
};

export default BioDataForm;
